import type { Settlement } from '../engine/map/settlement';
import { recordConsumed, recordProduced } from '../engine/map/settlement';
import { getGameConfig } from '../config/gameConfig';
import { getBuildingStorage, getEffectiveOutputCap } from '../engine/settlement/utils';

function getHuntableWildlife(settlement: Settlement): number {
    const huntableAgeMin = getGameConfig().wildLife.huntableAgeMin;
    return settlement.regionResources.wild_life
        .filter((entry) => entry.age >= huntableAgeMin)
        .reduce((sum, entry) => sum + entry.amount, 0);
}

function takeWildlife(settlement: Settlement, count: number): boolean {
    const huntableAgeMin = getGameConfig().wildLife.huntableAgeMin;
    let remaining = count;
    const buckets = settlement.regionResources.wild_life;
    buckets.sort((a, b) => b.age - a.age);
    for (let i = 0; i < buckets.length && remaining > 0; i += 1) {
        if (buckets[i].age < huntableAgeMin) continue;
        const take = Math.min(remaining, buckets[i].amount);
        buckets[i].amount -= take;
        remaining -= take;
    }
    for (let i = buckets.length - 1; i >= 0; i -= 1) {
        if (buckets[i].amount <= 0) buckets.splice(i, 1);
    }
    return remaining < count;
}

function getHuntTicks(huntable: number): number {
    const { ticksPerHuntRatio, minHuntTicks } = getGameConfig().production.hunter;
    if (huntable <= 0) return minHuntTicks;
    const ratio = ticksPerHuntRatio.ticks * (ticksPerHuntRatio.wildLife / huntable);
    const rounded = Math.max(minHuntTicks, Math.round(ratio * 10) / 10);
    return rounded;
}

export function runHuntTick(settlement: Settlement): boolean {
    const config = getGameConfig();
    const { foodPerWildLife, leatherPerWildLife, toolUsage } = config.production.hunter;
    const foodCap = getEffectiveOutputCap(settlement, config, 'huntingHut', 'food');
    const leatherCap = getEffectiveOutputCap(settlement, config, 'huntingHut', 'leather');
    const storage = getBuildingStorage(settlement, 'huntingHut');
    let changed = false;

    if (settlement.buildings.huntingHut.qty <= 0 || settlement.jobs.hunters <= 0) {
        const hadWork = settlement.production.hunt.progress.length > 0;
        settlement.production.hunt.progress = [];
        return hadWork;
    }

    while (settlement.production.hunt.progress.length < settlement.jobs.hunters) {
        settlement.production.hunt.progress.push(0);
        changed = true;
    }
    while (settlement.production.hunt.progress.length > settlement.jobs.hunters) {
        settlement.production.hunt.progress.pop();
        changed = true;
    }

    const toolCount = settlement.tools.hunters.length;
    const huntable = getHuntableWildlife(settlement);
    const requiredTicks = getHuntTicks(huntable);

    if (huntable > 0) {
        for (let i = 0; i < settlement.production.hunt.progress.length; i += 1) {
            if (i >= toolCount) continue;
            if (foodCap <= 0 || foodCap - storage.food < foodPerWildLife) break;
            if (leatherCap <= 0 || leatherCap - storage.leather < leatherPerWildLife) break;
            settlement.production.hunt.progress[i] += 1;
            if (settlement.production.hunt.progress[i] >= requiredTicks) {
                settlement.production.hunt.progress[i] -= requiredTicks;
                if (takeWildlife(settlement, 1)) {
                    storage.food += foodPerWildLife;
                    storage.leather += leatherPerWildLife;
                    recordProduced(settlement, 'food', foodPerWildLife);
                    recordProduced(settlement, 'leather', leatherPerWildLife);
                    changed = true;
                }
            }
        }

        for (let i = settlement.tools.hunters.length - 1; i >= 0; i -= 1) {
            settlement.tools.hunters[i].durability -= toolUsage;
            if (settlement.tools.hunters[i].durability <= 0) {
                recordConsumed(settlement, 'tools', 1);
                settlement.tools.hunters.splice(i, 1);
                changed = true;
            }
        }
    }

    return changed;
}
